import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { colors } from '../theme';
import BoltIcon from '@mui/icons-material/Bolt';
import WbSunnyIcon from '@mui/icons-material/WbSunny';

function HeroSection() {
  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      id="home"
      sx={{
        position: 'relative',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        backgroundImage: 'url(/herobackground.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.7) 0%, rgba(45, 45, 45, 0.6) 50%, rgba(26, 26, 26, 0.7) 100%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(0, 200, 83, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        },
      }}
    >

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            {/* Logo on Mobile - Above Title */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                component="img"
                src="/logo-white.png"
                alt="SolaCharge Logo"
                sx={{
                  height: '120px',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.4))',
                }}
              />
            </Box>

            {/* Main Headline */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h1"
                sx={{
                  color: colors.white,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  fontWeight: 800,
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                Smart, Green & EVCA-Compliant
                <Box
                  component="span"
                  sx={{
                    color: colors.yellow,
                    display: 'block',
                    mt: 1,
                  }}
                >
                  EV Charging for Singapore
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: colors.white,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  fontWeight: 400,
                  mb: 4,
                  opacity: 0.95,
                  maxWidth: '600px',
                }}
              >
                Integrated EV charging, solar solutions, and energy storage —
                from design to deployment.
              </Typography>
            </Box>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleNavClick('#contact')}
                sx={{
                  bgcolor: colors.yellow,
                  color: colors.black,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: colors.lightYellow,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${colors.yellow}40`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Free Consultation
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => handleNavClick('#contact')}
                sx={{
                  borderColor: colors.green,
                  color: colors.green,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: colors.green,
                    bgcolor: colors.green,
                    color: colors.white,
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Us
              </Button>
            </Box>

            {/* Trust Badges */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                mt: 5,
                pt: 4,
                borderTop: `4px solid ${colors.green}40`,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: colors.yellow,
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    mb: 0.5,
                  }}
                >
                  TR25:2022 (+A1:2025)
                </Typography>
                <Typography sx={{ color: colors.white, fontSize: '0.85rem', opacity: 0.8 }}>
                  Compliant
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.yellow,
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    mb: 0.5,
                  }}
                >
                  LTA EVCO
                </Typography>
                <Typography sx={{ color: colors.white, fontSize: '0.85rem', opacity: 0.8 }}>
                  Licensed Operator
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.yellow,
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    mb: 0.5,
                  }}
                >
                  P&EMS + DLB
                </Typography>
                <Typography sx={{ color: colors.white, fontSize: '0.85rem', opacity: 0.8 }}>
                  Smart Power Management
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            {/* Logo on Desktop - Right Side */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
              }}
            >
              <Box
                component="img"
                src="/logo-white.png"
                alt="SolaCharge Logo"
                sx={{
                  height: '280px',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.4))',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default HeroSection;
